import { type PaletteOptions } from "@mui/material";

const WARM_GREY_00_COLOR = "#FFFFFF";
// this name is unfortunate, but that's how they are in figma
const WARM_GREY_00_5_COLOR = "#FAF8F7";
const WARM_GREY_01_COLOR = "#F6F3F1";
const WARM_GREY_02_COLOR = "#F1EDEA";
const WARM_GREY_03_COLOR = "#E6E2DD";
const WARM_GREY_04_COLOR = "#D5D1CE";
const WARM_GREY_05_COLOR = "#B0AEAC";
const WARM_GREY_06_COLOR = "#8B8987";
const WARM_GREY_07_COLOR = "#3D414B";

// const COOL_GREY_01_COLOR = "#E9ECF2";
// const COOL_GREY_02_COLOR = "#D7DEE8";
const COOL_GREY_03_COLOR = "#B4BECD";
const COOL_GREY_04_COLOR = "#9CA9BD";
// const COOL_GREY_05_COLOR = "#7689A6";
const COOL_GREY_06_COLOR = "#3B4C65";
const COOL_GREY_07_COLOR = "#2F3C50";

// const BLUE_01_COLOR = "#E8F4FF";
const BLUE_02_COLOR = "#CCE7FF";
const BLUE_03_COLOR = "#B7DDFF";
const BLUE_04_COLOR = "#98CCFB";
const BLUE_05_COLOR = "#1240A1";
const BLUE_06_COLOR = "#082A74";
const BLUE_07_COLOR = "#0C1C43";
const BLUE_08_COLOR = "#020F2C";
const BLUE_09_COLOR = "#0D2B57";

// const PURPLE_01_COLOR = "#ECEBFF";
const PURPLE_02_COLOR = "#D7D4FF";
const PURPLE_03_COLOR = "#BDB8FE";
const PURPLE_04_COLOR = "#9890FF";
// const PURPLE_05_COLOR = "#7269F0";
// const PURPLE_06_COLOR = "#5851C6";
const PURPLE_07_COLOR = "#372FAA";

const COOL_PURPLE_01_COLOR = "#F1F0FF";
const COOL_PURPLE_02_COLOR = "#E2B8FF";

// const TEAL_01_COLOR = "#E1F5F6";
const TEAL_02_COLOR = "#C9F2F4";
const TEAL_03_COLOR = "#AEEDF1";
const TEAL_04_COLOR = "#75D1D6";
// const TEAL_05_COLOR = "#45C1C8";
const TEAL_06_COLOR = "#03929A";
const TEAL_07_COLOR = "#004345";

// const GREEN_01_COLOR = "#E0FBEF";
// const GREEN_02_COLOR = "#C0F6D4";
const GREEN_03_COLOR = "#ADF3C7";
const GREEN_04_COLOR = "#75DEAF";
const GREEN_05_COLOR = "#43C390";
const GREEN_06_COLOR = "#169563";
const GREEN_07_COLOR = "#094534";

const YELLOW_01_COLOR = "#FFFBF6";
const YELLOW_02_COLOR = "#FFF3E6";
const YELLOW_03_COLOR = "#FFE7BC";
const YELLOW_04_COLOR = "#FACE97";
const YELLOW_05_COLOR = "#FFC884";
const YELLOW_06_COLOR = "#BD6801";
const YELLOW_07_COLOR = "#764100";

// const RED_01_COLOR = "#FCF2F0";
const RED_02_COLOR = "#F9E1E1";
const RED_03_COLOR = "#F7D0D0";
// const RED_04_COLOR = "#FCBDC1";
const RED_05_COLOR = "#DB646A";
const RED_06_COLOR = "#B01921";
const RED_07_COLOR = "#82090F";

// const GOLD_01_COLOR = "#FFE8A3";
// const GOLD_02_COLOR = "#E6B423";
// const GOLD_03_COLOR = "#D3A136";
// const GOLD_04_COLOR = "#B07E24";

// const SILVER_01_COLOR = "#EBF2F7";
// const SILVER_02_COLOR = "#70808E";

const PINK_01_COLOR = "#FFCEE7";
const PINK_02_COLOR = "#A32265";

const ORANGE_01_COLOR = "#FFE3C3";

const MINT_01_COLOR = "#DAF1E8";

export const generateSpinnerGradient = (hexColor: string) =>
  `conic-gradient(from 90deg at 50% 50%, ${hexColor}00 0deg, ${hexColor}0A 0.04deg, ${hexColor}FF 360deg)`;

declare module "@mui/material/styles" {
  interface IntentColorsMap {
    /**
     * This is called `surface` in figma
     */
    background: string;
    /**
     * This is called `surfaceInvert` in figma
     */
    backgroundInvert: string;
    border: string;
    text: string;
    icon: string;
    gradient?: string;
  }

  // So far these are optional as module augmentation will affect the old theme and I want to avoid confusions
  interface CustomPalette {
    brand: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    border?: {
      primary: string;
      dark: string;
      subtle: string;
      primaryInvert: string;
      subtleInvert: string;
      darkInvert: string;
      main: string;
      strong: string;
    };
    icon?: {
      primary: string;
      disabled: string;
      lightInvert: string;
    };
    intent?: {
      neutral: IntentColorsMap;
      info: IntentColorsMap;
      success: IntentColorsMap;
      warning: IntentColorsMap;
      error: IntentColorsMap;
      disabled: IntentColorsMap;
    };
    instantPay?: {
      background: string;
      text: string;
    };

    /**
     * This is intentionally exported separately so in the future we have the possibility to split
     * base design system from hcp app.
     *
     * If we were to continue using this design system for other apps, we could easily maintain a
     * global one with generic colors and only export HCP and shifts specific stuff separately.
     */
    shift?: {
      urgent: IntentColorsMap & {
        gradient: string;
      };
      inAppAds: {
        text: string;
        background: string;
        timeSlotBackground: string;
      };
      bookingRequest: {
        text: string;
        background: string;
      };
      priority: IntentColorsMap & {
        gradient: string;
      };
      invite: IntentColorsMap & {
        gradient: string;
      };
      block: IntentColorsMap & {
        gradient: string;
      };
      day: {
        gradient: string;
      };
      evening: {
        gradient: string;
      };
      overnight: {
        gradient: string;
      };
      verified: {
        gradient: string;
      };
      unverified: {
        gradient: string;
      };
      inreview: {
        gradient: string;
      };
      badgeSkippedWarning: {
        gradient: string;
        warningColor: string;
      };
      badgePolicy: {
        successGradient: string;
      };
      transferable: {
        background: string;
        text: string;
      };
      bundle: {
        border: string;
        gradient: string;
      };
      double: {
        border: string;
        gradient: string;
        footerBackground: string;
        footerText: string;
      };
      forYou: {
        background: string;
        border: string;
      };
      history: {
        gradient: string;
      };
    };
    link?: {
      primary: string;
    };
    charting?: {
      review: {
        gradient: string;
      };
      recall: {
        gradient: string;
      };
      violation: {
        gradient: string;
      };
    };
    placement?: {
      feature: {
        gradient: string;
        background: string;
      };
      openHouse: {
        feature: {
          gradient: string;
          background: string;
        };
        gradient: string;
        border: string;
        text: string;
      };
      interviewDay: {
        feature: {
          gradient: string;
          background: string;
        };
      };
      apply: {
        feature: {
          background: string;
        };
      };
    };
    documents?: {
      neutral: {
        gradient: string;
      };
      warning: {
        gradient: string;
      };
      error: {
        gradient: string;
      };
    };
    onboarding?: {
      inProgress: {
        gradient: string;
      };
      error: {
        gradient: string;
      };
      neutral: {
        gradient: string;
      };
    };
    homeHealth?: {
      visit: {
        background: string;
        text: string;
        gradient: string;
      };
      advertisement: {
        text: string;
        gradient: string;
      };
    };
    arrivalTime?: {
      accent: string;
    };
    payment?: {
      background: string;
      text: string;
    };
    components?: {
      locationMarker: {
        background: string;
        foreground: string;
        border: string;
      };
      clusterMarker: {
        background: string;
        foreground: string;
      };
      spinner: {
        primary: string;
        secondary: string;
        destructive: string;
        invert: string;
        generateGradient: (hexColor: string) => string;
      };
      avatar: {
        primary: undefined;
        secondary: string;
        tertiary: string;
        quaternary: string;
        septenary: string;
      };
      circleProgress: {
        track: string;
        trackOpacity?: number;
        primary: string;
        secondary: string;
        warning: string;
      };
      holdToConfirmButton: {
        default: string;
        confirmed: string;
      };
    };
    banner?: {
      borderGradient: {
        background: string;
        border: string;
      };
    };
    legacyFullScreenDialogBackground: string;
    workplaceReferrals?: {
      referralGuide: {
        gradient: string;
      };
      stats: {
        success: {
          gradient: string;
        };
        pending: {
          gradient: string;
        };
      };
    };
    workplaceGroupChats?: {
      gradient: string;
    };
    cancellationProtection?: {
      gradient: string;
    };
  }

  // Actual MUI types
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Palette extends CustomPalette {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface PaletteOptions extends CustomPalette {}

  interface TypeText {
    primary: string;
    secondary: string;
    tertiary?: string;
    invertPrimary?: string;
    invertSecondary?: string;
    invertTertiary?: string;
  }

  interface TypeBackground {
    default: string;
    paper: string;
    primary: string;
    secondary: string;
    secondaryDark?: string;
    tertiary?: string;
    quaternary?: string;
    invert?: string;
  }
}

export const muiThemePalette: PaletteOptions = {
  brand: {
    main: BLUE_06_COLOR,
    light: BLUE_07_COLOR,
    dark: BLUE_05_COLOR,
    contrastText: WARM_GREY_00_COLOR,
  },
  /**
   * MUI by default handles e.g pressed/hover states by applying a darker shade of the color.
   *
   * In our designs we want to have a lighter shade for pressed/hover states, so we need to swap
   * dark and light.
   */
  primary: {
    main: BLUE_06_COLOR,
    contrastText: WARM_GREY_00_COLOR,
    dark: BLUE_05_COLOR,
    light: BLUE_07_COLOR,
  },
  secondary: {
    main: WARM_GREY_01_COLOR,
    contrastText: BLUE_07_COLOR,
  },
  error: {
    main: RED_06_COLOR,
    light: RED_07_COLOR,
    dark: RED_05_COLOR,
    contrastText: WARM_GREY_00_COLOR,
  },
  info: {
    main: BLUE_05_COLOR,
    light: BLUE_03_COLOR,
    dark: BLUE_04_COLOR,
    contrastText: WARM_GREY_00_COLOR,
  },
  warning: {
    main: YELLOW_04_COLOR,
    light: YELLOW_01_COLOR,
    dark: YELLOW_02_COLOR,
    contrastText: WARM_GREY_00_COLOR,
  },
  success: {
    main: GREEN_06_COLOR,
    light: GREEN_04_COLOR,
    dark: GREEN_05_COLOR,
    contrastText: WARM_GREY_00_COLOR,
  },
  background: {
    default: WARM_GREY_01_COLOR,
    paper: WARM_GREY_01_COLOR,
    primary: WARM_GREY_01_COLOR,
    secondary: WARM_GREY_02_COLOR,
    secondaryDark: WARM_GREY_03_COLOR,
    tertiary: WARM_GREY_00_COLOR,
    quaternary: WARM_GREY_00_5_COLOR,
    invert: BLUE_07_COLOR,
  },
  text: {
    primary: BLUE_07_COLOR,
    secondary: WARM_GREY_07_COLOR,
    tertiary: WARM_GREY_06_COLOR,
    disabled: WARM_GREY_05_COLOR,
    invertPrimary: WARM_GREY_00_COLOR,
    invertSecondary: COOL_GREY_03_COLOR,
    invertTertiary: COOL_GREY_04_COLOR,
  },
  action: {
    active: BLUE_06_COLOR,
  },
  divider: WARM_GREY_01_COLOR,
  border: {
    primary: WARM_GREY_04_COLOR,
    dark: WARM_GREY_05_COLOR,
    subtle: WARM_GREY_01_COLOR,
    primaryInvert: COOL_GREY_06_COLOR,
    subtleInvert: COOL_GREY_07_COLOR,
    darkInvert: COOL_GREY_03_COLOR,
    main: BLUE_07_COLOR,
    strong: BLUE_07_COLOR,
  },
  icon: {
    primary: BLUE_07_COLOR,
    disabled: WARM_GREY_06_COLOR,
    lightInvert: WARM_GREY_00_COLOR,
  },
  intent: {
    neutral: {
      background: BLUE_03_COLOR,
      backgroundInvert: BLUE_07_COLOR,
      border: BLUE_04_COLOR,
      text: BLUE_07_COLOR,
      icon: BLUE_07_COLOR,
      gradient: `radial-gradient(235.87% 108.53% at 11.87% -13.56%, #86C4FC 0%, #FDF2E9 100%)`,
    },
    info: {
      background: BLUE_03_COLOR,
      backgroundInvert: BLUE_07_COLOR,
      border: BLUE_04_COLOR,
      text: BLUE_07_COLOR,
      icon: BLUE_07_COLOR,
      gradient: "linear-gradient(115deg, #F1F8FE 12.88%, #B0D9FF 95.11%);",
    },
    success: {
      background: GREEN_03_COLOR,
      backgroundInvert: GREEN_06_COLOR,
      border: GREEN_04_COLOR,
      text: GREEN_07_COLOR,
      icon: GREEN_07_COLOR,
      gradient: `radial-gradient(86.48% 85.31% at 3.47% 14.69%, #E8FAE1 0%, #C9E9D9 72.21%, #ADD9BF 100%);`,
    },
    warning: {
      background: YELLOW_04_COLOR,
      backgroundInvert: YELLOW_06_COLOR,
      border: YELLOW_06_COLOR,
      text: YELLOW_07_COLOR,
      icon: YELLOW_07_COLOR,
      gradient: "linear-gradient(126deg, #FFF8F1 15.55%, #FEDCB5 97.06%)",
    },
    error: {
      background: RED_03_COLOR,
      backgroundInvert: RED_06_COLOR,
      border: RED_05_COLOR,
      text: RED_07_COLOR,
      icon: RED_06_COLOR,
      gradient: "linear-gradient(126deg, #FFF0F0 15.55%, #FFC8C8 97.06%)",
    },
    disabled: {
      background: WARM_GREY_01_COLOR,
      backgroundInvert: WARM_GREY_03_COLOR,
      border: WARM_GREY_01_COLOR,
      text: WARM_GREY_06_COLOR,
      icon: WARM_GREY_06_COLOR,
    },
  },
  instantPay: {
    background: GREEN_03_COLOR,
    text: GREEN_07_COLOR,
  },
  shift: {
    urgent: {
      background: YELLOW_04_COLOR,
      backgroundInvert: YELLOW_06_COLOR,
      border: YELLOW_05_COLOR,
      text: YELLOW_07_COLOR,
      icon: YELLOW_07_COLOR,
      gradient: "linear-gradient(166.67deg, #FFE3C3 5.67%, #F9F5F2 49.8%)",
    },
    inAppAds: {
      text: WARM_GREY_07_COLOR,
      background: `linear-gradient(180deg, ${WARM_GREY_00_5_COLOR} 0%, ${COOL_PURPLE_01_COLOR} 100%)`,
      timeSlotBackground: `radial-gradient(135% 100% at 30% 0%, ${PURPLE_03_COLOR} 0%, ${PURPLE_02_COLOR} 50%, ${COOL_PURPLE_02_COLOR} 100%)`,
    },
    bookingRequest: {
      background: YELLOW_03_COLOR,
      text: YELLOW_06_COLOR,
    },
    priority: {
      background: PURPLE_02_COLOR,
      backgroundInvert: PURPLE_07_COLOR,
      border: PURPLE_02_COLOR,
      text: PURPLE_07_COLOR,
      icon: PURPLE_07_COLOR,
      gradient: `linear-gradient(349.66deg, ${WARM_GREY_01_COLOR} 53.34%, ${PURPLE_03_COLOR} 93.29%)`,
    },
    invite: {
      background: PURPLE_02_COLOR,
      backgroundInvert: PURPLE_07_COLOR,
      border: PURPLE_02_COLOR,
      text: PURPLE_07_COLOR,
      icon: PURPLE_07_COLOR,
      gradient: `linear-gradient(349.66deg, ${WARM_GREY_01_COLOR} 53.34%, ${PURPLE_03_COLOR} 93.29%)`,
    },
    block: {
      background: TEAL_03_COLOR,
      backgroundInvert: TEAL_06_COLOR,
      border: TEAL_06_COLOR,
      text: TEAL_07_COLOR,
      icon: TEAL_07_COLOR,
      gradient: `linear-gradient(180deg, ${TEAL_03_COLOR} -5.14%, ${WARM_GREY_01_COLOR} 41.79%)`,
    },
    day: {
      gradient: `radial-gradient(143.38% 115.79% at -2.78% -4.17%, #FFD992 10%, #FFE9C9 48.5%, #FFA176 100%)`,
    },
    evening: {
      gradient: `radial-gradient(106.9% 107.64% at 0% 3.55%, #FBD0A8 0%, #F8B4B5 56.12%, #9AA2FA 100%)`,
    },
    forYou: {
      background: `linear-gradient(${WARM_GREY_00_5_COLOR}, ${WARM_GREY_00_5_COLOR}) padding-box, radial-gradient(106.9% 107.64% at 0% 3.55%, #FBD0A8 0%, #F8B4B5 56.12%, #9AA2FA 100%) border-box`,
      border: "1px solid transparent",
    },
    overnight: {
      gradient: `radial-gradient(86.48% 85.31% at 3.47% 14.69%, #C9D6FE 0%, #839EE8 72.21%, #5C78B4 100%)`,
    },
    transferable: {
      background: PINK_01_COLOR,
      text: PINK_02_COLOR,
    },
    bundle: {
      border: TEAL_04_COLOR,
      gradient: `radial-gradient(86.48% 85.31% at 3.47% 14.69%, #BBF2F5 0%, #A5EAEE 52.23%, #69D3DD 100%)`,
    },
    double: {
      border: "#B49DE6",
      gradient:
        "radial-gradient(86.48% 85.31% at 3.47% 14.69%, #E0D4F7 0%, #D1C1F0 52.23%, #B49DE6 100%)",
      footerBackground: "#F0EAFC",
      footerText: "#7C5CBF",
    },
    history: {
      gradient: `linear-gradient(124deg, ${MINT_01_COLOR} -30.06%, ${WARM_GREY_01_COLOR} 106.87%)`,
    },
    verified: {
      gradient: `linear-gradient(180deg, ${BLUE_04_COLOR} 19.5%, ${WARM_GREY_01_COLOR} 100%)`,
    },
    unverified: {
      gradient: `linear-gradient(180deg, ${RED_02_COLOR} 14.93%, ${WARM_GREY_01_COLOR} 75.39%)`,
    },
    inreview: {
      gradient: `linear-gradient(180deg, ${ORANGE_01_COLOR} 19.5%, ${WARM_GREY_01_COLOR} 100%)`,
    },
    badgeSkippedWarning: {
      gradient: "linear-gradient(-61deg, #FBF2E8 29%, #F7D0D0 67%)",
      warningColor: "#EF6D6E",
    },
    badgePolicy: {
      successGradient: "linear-gradient(180deg, #ADF3C7 1%, #E0FBEF 17%)",
    },
  },
  link: {
    primary: BLUE_09_COLOR,
  },
  charting: {
    review: {
      gradient: `linear-gradient(to bottom right, #FAECDE 0%, #FFE7BC 100%)`,
    },
    recall: {
      gradient: `linear-gradient(to bottom right, #FAECDE 0%, #F7D0D0 100%)`,
    },
    violation: {
      gradient: `linear-gradient(to bottom right, #FAECDE 0%, #F7D0D0 100%)`,
    },
  },
  placement: {
    feature: {
      gradient: `linear-gradient(110.79deg, #FAECDE 7.61%, ${PURPLE_04_COLOR} 96.83%)`,
      background: PURPLE_03_COLOR,
    },
    openHouse: {
      feature: {
        gradient: `linear-gradient(117.36deg, #FBF2E8 18.83%, ${PURPLE_02_COLOR} 96.06%)`,
        background: PURPLE_03_COLOR,
      },
      gradient: `linear-gradient(312.93deg, #FAF8F7 34.17%, #F1F0FF 88.75%);`,
      border: PURPLE_04_COLOR,
      text: PURPLE_07_COLOR,
    },
    interviewDay: {
      feature: {
        gradient: `linear-gradient(117.36deg, #FBF2E8 18.83%, ${BLUE_03_COLOR} 96.06%);`,
        background: BLUE_04_COLOR,
      },
    },
    apply: {
      feature: {
        background: TEAL_02_COLOR,
      },
    },
  },
  documents: {
    neutral: {
      gradient: `linear-gradient(117.36deg, #FBF2E8 18.83%, ${BLUE_03_COLOR} 96.06%);`,
    },
    warning: {
      gradient: `linear-gradient(117.36deg, #FBF2E8 18.83%, ${YELLOW_03_COLOR} 96.06%)`,
    },
    error: {
      gradient: `linear-gradient(117.36deg, #FBF2E8 18.83%, ${RED_03_COLOR} 96.06%)`,
    },
  },
  onboarding: {
    inProgress: {
      gradient: `linear-gradient(110.79deg, #FAECDE 0%, #DAF1E8 100%)`,
    },
    error: {
      gradient: `linear-gradient(110.79deg, #FAECDE 0%, ${RED_03_COLOR} 100%)`,
    },
    neutral: {
      gradient: "radial-gradient(235.87% 108.53% at 11.87% -13.56%, #86c4fc 0%, #fdf2e9 80%)",
    },
  },
  payment: {
    background: "linear-gradient(126deg, #FBF2E8 15.55%, #DEF1E8 97.06%)",
    text: TEAL_07_COLOR,
  },
  homeHealth: {
    visit: {
      background: TEAL_03_COLOR,
      text: TEAL_07_COLOR,
      gradient: `radial-gradient(86.48% 85.31% at 3.47% 14.69%, #BBF2F5 0%, #A5EAEE 52.23%, #69D3DD 100%)`,
    },
    advertisement: {
      text: BLUE_07_COLOR,
      gradient: `linear-gradient(110.79deg, #F9ECDE 7.61%, #DAF1E8 96.83%)`,
    },
  },
  arrivalTime: {
    accent: RED_05_COLOR,
  },
  components: {
    locationMarker: {
      background: PURPLE_03_COLOR,
      foreground: PURPLE_07_COLOR,
      border: WARM_GREY_00_COLOR,
    },
    clusterMarker: {
      background: WARM_GREY_00_COLOR,
      foreground: BLUE_07_COLOR,
    },
    spinner: {
      primary: BLUE_06_COLOR,
      secondary: BLUE_03_COLOR,
      destructive: RED_05_COLOR,
      invert: COOL_GREY_03_COLOR,
      generateGradient: generateSpinnerGradient,
    },
    avatar: {
      primary: undefined,
      secondary: BLUE_02_COLOR,
      tertiary: YELLOW_03_COLOR,
      quaternary: PURPLE_02_COLOR,
      septenary: RED_03_COLOR,
    },
    circleProgress: {
      track: COOL_GREY_07_COLOR,
      primary: BLUE_04_COLOR,
      secondary: BLUE_04_COLOR,
      warning: YELLOW_03_COLOR,
    },
    holdToConfirmButton: {
      default: BLUE_06_COLOR,
      confirmed: BLUE_08_COLOR,
    },
  },
  legacyFullScreenDialogBackground: WARM_GREY_01_COLOR,
  workplaceReferrals: {
    referralGuide: {
      gradient: `linear-gradient(90.67deg, ${YELLOW_03_COLOR} 5.65%, ${PURPLE_02_COLOR} 94.45%)`,
    },
    stats: {
      success: {
        gradient: `linear-gradient(to bottom, ${GREEN_03_COLOR}, white)`,
      },
      pending: {
        gradient: `linear-gradient(to bottom, ${BLUE_03_COLOR}, white)`,
      },
    },
  },
  workplaceGroupChats: {
    gradient: `linear-gradient(150deg, ${YELLOW_04_COLOR} 5.65%, ${COOL_PURPLE_02_COLOR} 94.45%)`,
  },
  cancellationProtection: {
    gradient: "linear-gradient(135deg, #F3EBAD 0%, #FFDBA1 100%)",
  },
};
